import { DatePicker, Dropdown, Space, Button, Typography } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarPlus,
  faGlobe,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import "dayjs/locale/it";
import {
  buildGoogleCalendarUrl,
  downloadICS,
  type CalendarEvent,
} from "../../utils/calendar";

dayjs.locale("it");

interface Props {
  weekNumber: number;
  date?: string | null;
  // When provided the week date is editable; omit for a read-only display.
  onChange?: (iso: string | null) => void;
  // Optional context (e.g. the programmazione name) woven into the event title.
  contextLabel?: string;
}

// Per-week date + "Aggiungi al calendario" control. Used inside a programmazione
// (detail/edit) so a specific week can be scheduled and exported to the user's
// device calendar (Apple/Outlook via .ics, Google via link). Not used in the
// precompiled library source.
export default function WeekDateControl({
  weekNumber,
  date,
  onChange,
  contextLabel,
}: Props) {
  const editable = typeof onChange === "function";

  const calendarEvent: CalendarEvent | null = date
    ? {
        title: `Allenamento — Settimana ${weekNumber}${
          contextLabel ? ` (${contextLabel})` : ""
        }`,
        date,
        description: "Settimana di allenamento programmata (Coaching Master).",
      }
    : null;

  return (
    <Space size={6} wrap>
      {editable ? (
        <DatePicker
          size="small"
          value={date ? dayjs(date) : null}
          onChange={(d) => onChange!(d ? d.startOf("day").toISOString() : null)}
          format="ddd D MMM YYYY"
          placeholder="Data settimana"
          data-testid={`week-date-picker-${weekNumber}`}
        />
      ) : date ? (
        <Typography.Text type="secondary" style={{ fontSize: 12, textTransform: "capitalize" }}>
          {dayjs(date).format("ddd D MMM YYYY")}
        </Typography.Text>
      ) : null}

      {calendarEvent && (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "google",
                icon: <FontAwesomeIcon icon={faGlobe} />,
                label: "Google Calendar",
                onClick: () =>
                  window.open(
                    buildGoogleCalendarUrl(calendarEvent),
                    "_blank",
                    "noopener,noreferrer"
                  ),
              },
              {
                key: "ics",
                icon: <FontAwesomeIcon icon={faDownload} />,
                label: "Apple / Outlook (.ics)",
                onClick: () => downloadICS(calendarEvent),
              },
            ],
          }}
        >
          <Button
            size="small"
            type="link"
            className="p-0"
            icon={<FontAwesomeIcon icon={faCalendarPlus} />}
            data-testid={`week-date-calendar-${weekNumber}`}
          >
            Aggiungi al calendario
          </Button>
        </Dropdown>
      )}
    </Space>
  );
}
